import { motion } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles, Target, Zap } from 'lucide-react';
import api from '@/utils/ky';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import ExpiredLinkMessage from '@/components/ExpiredLinkMessage';
import ExpiryLinkTimer from '@/components/ExpiryLinkTimer';
import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { constructLanguageUrl } from '../../router';

const features = (t: any) => [
  {
    icon: Sparkles,
    title: t('coach.landing.features.personalCoach.title'),
    description: t('coach.landing.features.personalCoach.description'),
    image:
      'https://tenorhq.com/wp-content/uploads/2024/03/conversation_feedback.png',
  },
  {
    icon: Target,
    title: t('coach.landing.features.realScenarios.title'),
    description: t('coach.landing.features.realScenarios.description'),
    image: 'https://tenorhq.com/wp-content/uploads/2024/03/custom_scenario.png',
  },
  {
    icon: Zap,
    title: t('coach.landing.features.immediateFeedback.title'),
    description: t('coach.landing.features.immediateFeedback.description'),
    image:
      'https://tenorhq.com/wp-content/uploads/2024/03/practice_conversation.png',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export default function CoachLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const posthog = usePostHog();
  const { lang } = useParams();

  const { t } = useTranslation();

  const { isFetched: isCheckCodeQueryFetched, data } = useQuery({
    queryKey: ['coach', 'check-code'],
    enabled: searchParams.has('code'), // Only run query if code param exists
    placeholderData: {
      isValid: false,
      validityInMinutes: 0,
      isExpired: true,
    },
    queryFn() {
      return api
        .get('api/public/verify', {
          searchParams: {
            code: searchParams.get('code'),
          },
        })
        .json<{
          isValid: boolean;
          error?: string;
          validityInMinutes: number;
          expiresAt?: number;
          isExpired?: boolean;
        }>();
    },
  });

  const startFreeTrial = () => {
    if (!isCheckCodeQueryFetched || !data.isValid) {
      return;
    }

    posthog.capture('coach_start_trial_clicked', {
      code: searchParams.get('code'),
      validityInMinutes: data.validityInMinutes,
    });

    const pathToChat = constructLanguageUrl(
      lang,
      '/coach/categories',
      `?${searchParams.toString()}`,
    );
    navigate(pathToChat);
  };

  const requestAccessForm = useMemo(
    () => (
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        // onSubmit={handleSubmit}
        className="mt-12 bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md mx-auto"
      >
        <h2 className="text-2xl font-medium mb-2">Request access</h2>
        <p className="text-gray-600 text-sm mb-6">
          {t('coach.landing.requestAccess.subtitle')}
        </p>
        {/* {emailError && (
        <p className="text-red-500 text-sm mb-2">{emailError}</p>
      )} */}
        <Input
          type="email"
          placeholder="Enter your email"
          name="email"
          required
          className="max-w-xs mx-auto"
          // aria-invalid={emailError ? 'true' : 'false'}
        />
        <Button type="submit" className="mt-6 w-full max-w-xs text-white">
          {t('coach.landing.requestAccess.title')}
        </Button>
      </motion.form>
    ),
    [],
  );

  return (
    <>
      <div className="min-h-screen bg-[#FAFAFA] relative overflow-hidden">
        {searchParams.has('code') &&
          (isCheckCodeQueryFetched ? (
            data.isExpired ? (
              <ExpiredLinkMessage />
            ) : data?.expiresAt &&
              data.expiresAt - Date.now() < 6 * 60 * 60 * 100 ? (
              <ExpiryLinkTimer sessionEndsAt={data.expiresAt} />
            ) : null
          ) : null)}

        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -right-1/2 w-[1000px] h-[1000px] bg-gradient-to-b from-primary/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -left-1/2 w-[800px] h-[800px] bg-gradient-to-t from-blue-50/30 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative">
          <div className="px-6 pt-20 lg:pt-32 lg:px-8 mb-32">
            <div className="mx-auto max-w-5xl text-center">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-12"
              >
                <motion.img
                  src="https://coach.hupo.co/logos/trademarked/orange/logo.svg"
                  alt="Hupo AI"
                  className="h-16 w-auto mx-auto"
                />
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-6xl lg:text-7xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent"
              >
                {t('coach.landing.title1')}
                <br />
                {t('coach.landing.title2')}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-8 text-xl text-gray-600 max-w-2xl mx-auto"
              >
                {t('coach.landing.description')}
              </motion.p>

              {!isCheckCodeQueryFetched || !data.isValid ? null : ( // requestAccessForm
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="mt-12"
                >
                  <Button
                    onClick={startFreeTrial}
                    className="px-8 py-6 text-lg bg-primary hover:bg-primary-600 rounded-full transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-primary/20"
                  >
                    {t('coach.landing.startTrial')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </motion.div>
              )}
            </div>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="px-6 py-20 bg-white"
          >
            <div className="mx-auto max-w-7xl">
              <div className="grid md:grid-cols-3 gap-12">
                {features(t).map((feature) => (
                  <motion.div
                    key={feature.title}
                    variants={itemVariants}
                    className="group relative"
                  >
                    <div className="relative overflow-hidden rounded-2xl">
                      <div className="absolute inset-0 bg-gradient-to-b from-black/0 to-black/60 z-10" />
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="w-full aspect-[4/3] object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                        <div className="flex items-center gap-3 mb-3">
                          <feature.icon className="h-6 w-6 text-primary" />
                          <h3 className="text-xl font-semibold text-white">
                            {feature.title}
                          </h3>
                        </div>
                        <p className="text-gray-200 text-sm">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
